import { FunctionComponent } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleCheck, faCircleXmark } from '@fortawesome/pro-solid-svg-icons';
import Client, { LeadStage } from '@spike/client-model';
import clsx from 'clsx';
import { buildLeadStageSteps } from './leadStages';
import { formatLeadDateTime } from '../leadMetrics';
import styles from './LeadStagesCard.module.scss';

interface LeadStagesCardProps {
    lead: Client;
    timeZone: string;
}

const STAGE_CLASSES: Record<LeadStage, string> = {
    [LeadStage.New]: styles.stageNew,
    [LeadStage.Contacted]: styles.stageContacted,
    [LeadStage.Booked]: styles.stageBooked,
    [LeadStage.Won]: styles.stageWon,
    [LeadStage.Lost]: styles.stageLost
};

const LeadStagesCard: FunctionComponent<LeadStagesCardProps> = ({ lead, timeZone }) => {
    const steps = buildLeadStageSteps(lead);
    const clientSince = lead.clientSince ? formatLeadDateTime(lead.clientSince, timeZone) : undefined;

    return (
        <section className={styles.card}>
            <h3 className={styles.title}>Stages</h3>

            <ol className={styles.steps}>
                {steps.map((step, index) => (
                    <li
                        key={step.stage}
                        className={clsx(styles.step, step.reached ? STAGE_CLASSES[step.stage] : styles.stepUnreached)}
                    >
                        <span className={styles.stepLabelRow}>
                            {step.reached ? (
                                <FontAwesomeIcon
                                    className={styles.stepIcon}
                                    icon={step.stage === LeadStage.Lost ? faCircleXmark : faCircleCheck}
                                />
                            ) : (
                                // Only rendered on mobile, where the progress bars give way to
                                // numbered steps.
                                <span className={styles.stepNumber}>{index + 1}</span>
                            )}
                            <span className={styles.stepLabel}>{step.label}</span>
                        </span>
                        <span className={styles.stepBar} />
                    </li>
                ))}
            </ol>
            {clientSince && (
                <div className={styles.clientSince}>
                    <span className={styles.clientSinceLabel}>Client Since: </span>
                    <span className={styles.clientSinceValue}>
                        {clientSince.date} · {clientSince.time}
                    </span>
                </div>
            )}
        </section>
    );
};

export default LeadStagesCard;
